import React, { useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { MultiValue } from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
// Api
import { GET_ALL_HASHTAGS } from 'api/hashtags/queries';
// Types
import { SortDirection } from 'api/graphql-global-types';
import {
  GetAllHashtags,
  GetAllHashtagsVariables,
} from 'api/hashtags/types/GetAllHashtags';
import { useApolloClient } from '@apollo/client';

export type HashtagOption = {
  label: string;
  value: string;
  storeCount: number;
};

type HashtagSelectProps = {
  name: string;
  value: HashtagOption[];
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  className?: string;
  error?: string | boolean;
  onChange: (hashtags: HashtagOption[]) => void;
};

const HashtagSelect: React.FC<HashtagSelectProps> = ({
  name,
  value,
  disabled = false,
  label,
  placeholder = 'Search hashtags',
  className,
  error,
  onChange,
}) => {
  const client = useApolloClient();

  const debouncedFetch = useDebouncedCallback(
    async (
      inputValue: string,
      callback: (options: HashtagOption[]) => void
    ) => {
      try {
        const { data } = await client.query<
          GetAllHashtags,
          GetAllHashtagsVariables
        >({
          query: GET_ALL_HASHTAGS,
          variables: {
            input: {
              keyword: inputValue,
              limit: 10,
              offset: 0,
              direction: SortDirection.DESC,
            },
          },
        });
        const hashtags =
          data?.getAllHashtags?.entities.map((hashtag: any) => ({
            label: hashtag.name,
            value: String(hashtag.id),
            storeCount: hashtag.storeCount,
          })) || [];
        callback(hashtags);
      } catch (error) {
        console.error('Error fetching hashtags:', error);
        callback([]);
      }
    },
    300
  );

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: HashtagOption[]) => void) => {
      if (!inputValue) {
        callback([]);
        return;
      }
      debouncedFetch(inputValue, callback);
    },
    [debouncedFetch]
  );

  const handleChange = (selected: MultiValue<HashtagOption>) => {
    onChange(Array.isArray(selected) ? [...selected] : []);
  };

  return (
    <div className={className}>
      {label && <label htmlFor={name}>{label}</label>}
      <AsyncSelect
        inputId={name}
        isMulti
        isDisabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        loadOptions={loadOptions}
        cacheOptions
        defaultOptions={false}
        getOptionValue={(option) => String(option.value)}
        getOptionLabel={(option) => option.label}
        noOptionsMessage={() => 'Start typing to search hashtags'}
        styles={
          error ? { control: (base) => ({ ...base, borderColor: 'red' }) } : {}
        }
      />
      {error && typeof error === 'string' && (
        <div style={{ color: 'red', fontSize: 12 }}>{error}</div>
      )}
    </div>
  );
};

export default HashtagSelect;
